<template>
  <multi-select ref="select"
                :class="{'hide-tags': hideTags}"
                :allow-empty="allowEmpty"
                :close-on-select="closeOnSelect"
                :deselect-label="deselectLabel"
                :disabled="disabled"
                :error="error"
                :group-label="groupLabel"
                :group-select="groupSelect"
                :group-values="groupValues"
                :hide-selected="hideSelected"
                :blockKeys="['Delete']"
                :id="id"
                :internal-search="!isAsync && internalSearch"
                :label="label"
                :loading="loading"
                :multiple="multiple"
                :options="filteredOptions"
                :placeholder="placeholder"
                :searchable="searchable"
                :select-label="selectLabel"
                :selected-label="selectedLabel"
                :taggable="taggable"
                :tag-placeholder="tagPlaceholder"
                :tag-position="tagPosition"
                :track-by="valueKey"
                :value="multiSelectValue"
                @open="$emit('open', $event)"
                @change="$emit('change', $event)"
                @search-change="handleAsyncSearch"
                @select="$emit('select', $event)"
                @remove="$emit('remove', $event)"
                @input="handleInput"
                @tag="handleAddTag">
    <template slot="option" slot-scope="{option, search}">
      <slot name="option" v-bind="{option, search}">
        <span :class="{ hiddenOption: option.hidden }">{{ getOptionDisplayValue(option) }}</span> <span v-if="option.hidden" class="hiddenOption">&nbsp;&nbsp;(PHASED OUT)</span>
      </slot>
    </template>

    <template slot="singleLabel" slot-scope="{option}">
      <slot name="singleLabel" v-bind="{option}">
        {{ getOptionDisplayValue(option) }}
      </slot>
    </template>

    <template slot="noResult">
      <slot name="noResult">
        <span>No result found. Consider changing the search value</span>
      </slot>
    </template>

  </multi-select>
</template>

<script>
  import axios from 'axios'
  import debounce from 'lodash/debounce'
  import find from 'lodash/find'
  import sortBy from 'lodash/sortBy'
  import values from 'lodash/values'

  import {deepFilterList} from "../utilities/utilityOperators";

  import MultiSelect from 'vue-multiselect'
  import 'vue-multiselect/dist/vue-multiselect.min.css'

  const debouncedAsyncRequest = debounce(function (url, params) {
    axios.get(url, params)
      .then(({data}) => {
        this.asyncResults = data || [];
        this.performedAsyncSearch = true;
        this.loading = false;
      });
  }, 400);

  export default {
    name: "smd-select",
    components: {MultiSelect},
    props: {
      allowEmpty: {type: Boolean, default: true},
      asyncEndpoint: {type: String, default: ''},
      asyncSearchParamKey: {type: String, default: 'q'},
      closeOnSelect: {type: Boolean, default: true},
      customTagLabel: {type: String, default: 'others'},
      deselectLabel: {type: String, default: ''},
      disabled: {type: Boolean, default: false},
      error: {type: Boolean, default: false},
      groupLabel: {type: String, default: 'name'},
      groupSelect: {type: Boolean, default: false},
      groupValues: {type: String, default: 'conditions'},
      searchLabel: {type: String, default: 'name'},
      searchValues: {type: String, default: 'conditions'},
      hideSelected: {type: Boolean, default: false},
      hideTags: {type: Boolean, default: false},
      id: {type: String},
      internalSearch: {type: Boolean, default: false},
      label: {type: String, default: 'name'},
      multiple: {type: Boolean, default: true},
      options: {required: true},
      placeholder: {type: String, default: ''},
      selectLabel: {type: String, default: ''},
      selectedLabel: {type: String, default: ''},
      searchable: {type: Boolean, default: true},
      sortKey: String,
      taggable: {type: Boolean, default: false},
      tagPlaceholder: {type: String, default: 'Press enter to add to list'},
      tagPosition: {type: String, default: 'bottom'},
      value: {required: true},
      valueEmitAsId: {type: Boolean, default: false},
      valueKey: {type: String, default: 'conditionId'}
    },
    data() {
      return {
        asyncResults: [],
        internalQuery: null,
        performedAsyncSearch: false,
        loading: false,
      };
    },
    computed: {
      filteredOptions() {
        const q = this.internalQuery;

        if (this.isAsync && this.performedAsyncSearch) {
          return this.asyncResults;
        }
        else if(typeof q === 'string' && q.length > 0) {
          return deepFilterList(true)(this.searchLabel)(this.searchValues)(this.options)(this.internalQuery);
          //return deepFilterList(true)(this.groupLabel)(this.groupValues)(this.options)(this.internalQuery);
        }

        return this.optionsAsArray;
      },
      isAsync() {
        return typeof this.asyncEndpoint === 'string' && this.asyncEndpoint.length > 0;
      },
      isOptionValuePrimitive() {
        return this.optionsAsArray.length > 0 && typeof this.optionsAsArray[0] !== 'object';
      },
      optionsAsArray() {
        let arr = [];
        if(this.options instanceof Array) {
          arr = this.options;
        } else {
          arr = values(this.options);
          if (this.sortKey) {
            sortBy(arr, this.sortKey);
          }
        }

        return arr;
      },
      multiSelectValue() {
        if (!this.value) return null;

        if (this.valueEmitAsId) {
          const lookup = this.performedAsyncSearch ? this.asyncResults : this.options;

          if (lookup instanceof Array) {
            return find(lookup, (v) => v[this.valueKey] === this.value);
          } else {
            return lookup[this.value];
          }
        }

        return this.value;
      }
    },
    methods: {
      _debouncedInternalSearch: debounce(function(q) {
        this.internalQuery = q;
      }, 350),
      getOptionDisplayValue(o, x) {
        return typeof o === 'string' ? o : (o.$groupLabel || o[this.label]);
      },
      handleAddTag(q) {
        if (this.isOptionValuePrimitive) {
          this.handleInput(q);
        } else {
          this.handleInput({
            [this.valueKey]: null,
            [this.customTagLabel]: q,
            [this.label]: q
          });
        }
      },
      handleAsyncSearch(q) {
        this.$emit('search-change', q);

        if(!this.internalSearch) {
          this._debouncedInternalSearch(q);
          return;
        }

        if (!this.isAsync || !this.$refs.select.isOpen) return;

        //handle async source
        this.loading = true;
        const params = {
          [this.asyncSearchParamKey]: q
        };

        debouncedAsyncRequest.bind(this)(this.asyncEndpoint, {params});
      },
      handleInput(value) {
        this.$emit('input', value);
        /*
        let val = null;

        if (value instanceof Array) {
          val = value;
        } else if (value != null && value !== undefined) {
          let emitValue = !this.isOptionValuePrimitive && this.valueEmitAsId ? value[this.valueKey] : value;

          if (this.multiple) {
            const arr = this.value.slice(0);
            arr.push(emitValue);
            val = arr;
          } else {
            val = emitValue;
          }
        }

        this.$emit(input, val);
        */
      }
    },
  }
</script>

<style lang="scss">
  .multiselect__tags{
    padding: 1px 0 0 0;
  }

  .hiddenOption {
    font-style: italic;
  }

  .hide-tags {
    .multiselect__tag {
      display: none;
    }

    .multiselect__input {
      background: none !important;
      border: none !important;
      width: 96% !important;
      margin: 0;
    }
  }
</style>
